import { z } from 'zod';
export const footballPublicSchemaVersion = 'football-public-api@1';
export const opaquePublicIdSchema = z.string().trim().min(1).max(128);
export const publicRequestIdSchema = z.string().trim().min(1).max(256);
export const canonicalUtcInstantSchema = z.string().refine((value) => {
    const timestamp = new Date(value);
    return !Number.isNaN(timestamp.valueOf()) && timestamp.toISOString() === value;
}, 'Expected a canonical RFC 3339 UTC instant');
export const calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const timestamp = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(timestamp.valueOf()) && timestamp.toISOString().startsWith(value);
}, 'Expected a calendar date');
export const publicCompetitionCodeSchema = z.string().trim().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
export const matchViewSchema = z.enum(['live', 'upcoming', 'finished', 'changes']);
export const fixtureStateSchema = z.enum([
    'scheduled',
    'kickoff_tbd',
    'first_half',
    'half_time',
    'second_half',
    'extra_time',
    'penalties',
    'finished',
    'finished_after_extra_time',
    'finished_after_penalties',
    'postponed',
    'cancelled',
    'abandoned',
    'interrupted',
    'suspended',
    'unknown',
]);
export const publicFreshnessSchema = z.object({
    state: z.enum(['fresh', 'stale', 'unavailable', 'provider_failed']),
    source: z.enum(['api_football', 'ballpeek_projection', 'ballpeek_historical_archive']),
    observedAt: canonicalUtcInstantSchema.nullable(),
    projectedAt: canonicalUtcInstantSchema,
    staleAfterSeconds: z.number().int().min(0),
    lastGoodAvailable: z.boolean(),
}).strip();
export const competitionRefSchema = z.object({
    competitionCode: publicCompetitionCodeSchema,
    competitionName: z.string().trim().min(1).max(160),
    season: z.number().int(),
    seasonLabel: z.string().trim().min(1).max(80),
}).strip();
export const teamRefSchema = z.object({
    teamId: opaquePublicIdSchema,
    name: z.string().trim().min(1).max(200),
    badgeUrl: z.url().startsWith('https://').max(2048).nullable(),
}).strip();
export const scoreSchema = z.object({
    home: z.number().int().min(0),
    away: z.number().int().min(0),
    basis: z.enum(['current', 'regular_time', 'after_extra_time', 'after_penalties']),
    halfTime: z.object({ home: z.number().int().min(0), away: z.number().int().min(0) }).strip().nullable(),
    extraTime: z.object({ home: z.number().int().min(0), away: z.number().int().min(0) }).strip().nullable(),
    penalties: z.object({ home: z.number().int().min(0), away: z.number().int().min(0) }).strip().nullable(),
}).strip();
export const scheduleChangeSchema = z.object({
    kind: z.enum(['postponed', 'cancelled', 'abandoned', 'interrupted', 'suspended', 'unknown']),
    rescheduledKickoffAt: canonicalUtcInstantSchema.nullable(),
}).strip();
export const matchCardSchema = z.object({
    matchId: opaquePublicIdSchema,
    competition: competitionRefSchema,
    kickoffAt: canonicalUtcInstantSchema.nullable(),
    state: fixtureStateSchema,
    view: matchViewSchema,
    liveMinute: z.number().int().min(0).max(130).nullable(),
    stoppageMinute: z.number().int().min(0).max(30).nullable(),
    homeTeam: teamRefSchema,
    awayTeam: teamRefSchema,
    score: scoreSchema.nullable(),
    scheduleChange: scheduleChangeSchema.nullable(),
    freshness: publicFreshnessSchema,
    notableChange: z.object({
        kind: z.enum(['score_changed', 'status_changed', 'kickoff_changed']),
        occurredAt: canonicalUtcInstantSchema,
    }).strip().nullable(),
}).strip();
export function isIanaTimeZone(value) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
        return true;
    }
    catch {
        return false;
    }
}
export const ianaTimeZoneSchema = z.string()
    .trim()
    .min(1)
    .max(64)
    .refine(isIanaTimeZone, 'Expected an IANA time zone');
export const matchesQuerySchema = z.object({
    view: matchViewSchema.optional().default('live'),
    competitionCode: publicCompetitionCodeSchema.optional(),
    season: z.coerce.number().int().optional(),
    teamId: opaquePublicIdSchema.optional(),
    date: calendarDateSchema.optional(),
    timeZone: ianaTimeZoneSchema.optional().default('UTC'),
    page: z.coerce.number().int().min(1).max(1000).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).optional().default(25),
}).strict().superRefine((query, context) => {
    if ((query.competitionCode === undefined) !== (query.season === undefined)) {
        context.addIssue({
            code: 'custom',
            path: query.competitionCode === undefined ? ['competitionCode'] : ['season'],
            message: 'competitionCode and season must be provided together',
        });
    }
});
export const pageMetaSchema = z.object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    totalItems: z.number().int().min(0),
    totalPages: z.number().int().min(0),
    hasNextPage: z.boolean(),
}).strip();
export const publicMetaSchema = z.object({
    schemaVersion: z.literal(footballPublicSchemaVersion),
    generatedAt: canonicalUtcInstantSchema,
}).strip();
export function publicEnvelopeSchema(dataSchema) {
    return z.object({
        data: dataSchema,
        meta: publicMetaSchema,
    }).strip();
}
export const publicSectionReasonSchema = z.enum([
    'not_covered',
    'not_announced',
    'not_available',
    'out_of_date',
    'temporarily_unavailable',
]);
export function publicSectionSchema(dataSchema) {
    return z.object({
        data: dataSchema.nullable(),
        freshness: publicFreshnessSchema,
        reason: publicSectionReasonSchema.nullable(),
    }).strip();
}
export const historicalTeamRefSchema = z.object({
    teamId: opaquePublicIdSchema.nullable(),
    name: z.string().trim().min(1).max(200),
    badgeUrl: z.url().startsWith('https://').max(2048).nullable(),
}).strip();
export const playerRefSchema = z.object({
    playerId: opaquePublicIdSchema,
    name: z.string().trim().min(1).max(200),
    photoUrl: z.url().startsWith('https://').max(2048).nullable(),
    position: z.string().max(120).nullable(),
}).strip();
export const matchesResponseSchema = z.object({
    data: z.object({
        view: matchViewSchema,
        filters: z.object({
            competitionCode: publicCompetitionCodeSchema.nullable(),
            season: z.number().int().nullable(),
            teamId: opaquePublicIdSchema.nullable(),
            date: calendarDateSchema.nullable(),
            timeZone: z.string().trim().min(1).max(64),
        }).strip(),
        items: z.array(matchCardSchema),
        pagination: pageMetaSchema,
        noLiveRecovery: z.object({
            upcoming: z.array(matchCardSchema).max(3),
        }).strip().nullable(),
        scopeFreshness: publicFreshnessSchema,
    }).strip(),
    meta: publicMetaSchema,
}).strip();
export const publicErrorCodeSchema = z.enum([
    'auth_forbidden',
    'validation_error',
    'resource_not_found',
    'resource_identity_collision',
    'read_model_unavailable',
    'internal_error',
]);
export const publicErrorSchema = z.object({
    success: z.literal(false),
    error: z.object({
        issues: z.array(z.object({
            code: publicErrorCodeSchema,
            path: z.array(z.union([z.string(), z.number()])),
            message: z.string().optional(),
        }).strip()).min(1),
        name: z.literal('Error'),
    }).strip(),
}).strip();
